import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { useIsOfferLocked } from '@/hooks/use-is-offer-locked';
import { useToast } from '@/hooks/use-toast';
import {
  ShippingPolicy,
  ShippingPolicyState,
} from '@/lib/types/shipping-policy';
import {
  toShippingPolicyState,
  toShippingPolicyView,
} from '@/lib/utils/shipping-policy-state';
import { areAllShippingFeesZero } from '@/lib/utils/shipping-policy-utils';
import { productsState } from '@/states/products.state';
import { shippingPolicyState } from '@/states/shipping-policy.state';
import { useMutation } from '@apollo/client/react';
import { useAtom, useAtomValue } from 'jotai';

import { dealAtom, dealRefetchAtom } from '../../states/deal.state';
import {
  DealShippingPolicy,
  DealShippingPolicyInput,
  EditDealShippingPolicyDocument,
} from '../graphql/generated/graphql';
import { useDealNotEditableHandler } from './useDealNotEditableHandler';

const toStateFormat = (apiData: DealShippingPolicy): ShippingPolicyState => ({
  freeShippingRules:
    apiData.freeShippingRules?.map((rule) => ({
      countries: rule.countries ?? null,
      deliveryMethod: rule.deliveryMethod,
      freeShippingMinimumOrderAmount: rule.freeShippingMinimumOrderAmount,
      commercialOfferMinimumOrderAmount:
        rule.commercialOfferMinimumOrderAmount ?? null,
    })) ?? [],
  deliveryOfferedNumberOfDays: apiData.deliveryOfferedNumberOfDays ?? null,
  pickupPointNetwork: apiData.pickupPointNetwork ?? null,
  basicFrPickupPointDeliveryPrice:
    apiData.basicFrPickupPointDeliveryPrice ?? null,
  allShippingFeesZero: false,
});

export const toUIFormat = (apiData: DealShippingPolicy): ShippingPolicy =>
  toShippingPolicyView(toStateFormat(apiData));

export const toAPIFormat = (
  uiData: ShippingPolicy
): DealShippingPolicyInput => {
  const state = toShippingPolicyState(uiData);

  return {
    freeShippingRules: state.freeShippingRules,
    deliveryOfferedNumberOfDays: state.deliveryOfferedNumberOfDays,
    pickupPointNetwork: state.pickupPointNetwork,
    basicFrPickupPointDeliveryPrice: state.basicFrPickupPointDeliveryPrice,
  };
};

export function useShippingPolicy(dealId: string) {
  const [shippingPolicy, setShippingPolicy] = useAtom(shippingPolicyState);
  const dealItems = useAtomValue(productsState);
  const isOfferLocked = useIsOfferLocked();
  const { t } = useTranslation();
  const { toast } = useToast();
  const lastCommittedPolicyRef = useRef<ShippingPolicy>(
    toShippingPolicyView(shippingPolicy)
  );

  const deal = useAtomValue(dealAtom);
  const dealRefetch = useAtomValue(dealRefetchAtom);
  const shippingPolicyGraphQL = deal?.dealShippingPolicy;
  const isConfigured = shippingPolicyGraphQL?.isConfigured ?? false;

  const allShippingFeesZero = useMemo(
    () => areAllShippingFeesZero(dealItems),
    [dealItems]
  );

  useEffect(() => {
    setShippingPolicy((prev) => {
      if (shippingPolicyGraphQL) {
        const nextPolicy = toStateFormat(shippingPolicyGraphQL);
        return {
          ...nextPolicy,
          allShippingFeesZero,
        };
      }

      if (prev.allShippingFeesZero === allShippingFeesZero) {
        return prev;
      }

      return {
        ...prev,
        allShippingFeesZero,
      };
    });
  }, [shippingPolicyGraphQL, allShippingFeesZero, setShippingPolicy]);

  const resolvedPolicy = useMemo(() => {
    if (shippingPolicyGraphQL) {
      return toShippingPolicyView({
        ...toStateFormat(shippingPolicyGraphQL),
        allShippingFeesZero,
      });
    }
    return toShippingPolicyView({
      ...shippingPolicy,
      allShippingFeesZero,
    });
  }, [shippingPolicyGraphQL, allShippingFeesZero, shippingPolicy]);

  useEffect(() => {
    lastCommittedPolicyRef.current = resolvedPolicy;
  }, [resolvedPolicy]);

  const [editDealShippingPolicy] = useMutation(EditDealShippingPolicyDocument);
  const { handleIfDealNotEditable } = useDealNotEditableHandler();

  const handleShippingPolicyChange = useCallback(
    async (newPolicy: ShippingPolicy): Promise<boolean> => {
      if (isOfferLocked) {
        return false;
      }

      const previousPolicy = lastCommittedPolicyRef.current;
      setShippingPolicy(toShippingPolicyState(newPolicy));

      try {
        const result = await editDealShippingPolicy({
          variables: {
            dealId,
            shippingPolicy: toAPIFormat(newPolicy),
          },
        });

        const payload = result.data?.editDealShippingPolicy;
        if (payload?.__typename === 'DomainException') {
          handleIfDealNotEditable(payload.code);
          setShippingPolicy(toShippingPolicyState(previousPolicy));
          return false;
        }

        // Explicitly refetch deal to sync dealAtom with the new shipping policy.
        // Catch to avoid triggering the outer catch on a refetch network failure.
        await dealRefetch.fn().catch(() => {});
        return true;
      } catch {
        // Revert optimistic UI update on transport/runtime failures.
        setShippingPolicy(toShippingPolicyState(previousPolicy));
        toast({
          title: t('general.error'),
          description: t('general.unexpected_error'),
          variant: 'destructive',
        });
        return false;
      }
    },
    [
      dealId,
      dealRefetch,
      editDealShippingPolicy,
      handleIfDealNotEditable,
      isOfferLocked,
      setShippingPolicy,
      t,
      toast,
    ]
  );

  return {
    shippingPolicy: resolvedPolicy,
    isConfigured,
    handleShippingPolicyChange,
  };
}
